import { useCallback, useEffect } from 'react';
import type { MetaAdAccountSummary } from '@/services/api/metaAds';
import { formatAccountId as formatMetaAccountId } from '@/utils/metaAds';
import styles from '../GoogleAdsLinker/PollingStates.module.css';
import modalStyles from '@/styles/ModalStyles.module.css';

interface MetaLinkSuccessProps {
  account: MetaAdAccountSummary;
  onContinue: () => void;
}

/**
 * Success screen rendered after a Meta ad account is successfully linked.
 * Mirrors `GoogleAdsLinker/PollingSuccess.tsx` — same modal shell
 * (`ModalStyles` + `PollingStates`), same "Continue to Dashboard" CTA;
 * no auto-navigate (explicit click matches Google). By the time this
 * modal renders, `POST /meta/complete-setup` has already triggered the
 * Cloud Run backfill (synchronously, with `should_wait_for_data_sync()`
 * True), so campaign/insights data is available immediately — matching
 * Google's contract.
 */
export function MetaLinkSuccess({ account, onContinue }: MetaLinkSuccessProps) {
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') onContinue();
    },
    [onContinue],
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const modalTitleId = 'meta-link-success-title';
  // Centralized via `utils/metaAds.ts:formatAccountId` — strip the
  // `act_` namespace prefix everywhere the ID is surfaced to a user
  // (see also Home dashboard + MetaCampaignCard). The backend keeps
  // the prefix.
  const displayAccountId = formatMetaAccountId(account.meta_ad_account_id);
  const displayName = account.account_name || displayAccountId;
  const hasDetailLine =
    Boolean(account.account_name) ||
    Boolean(account.currency) ||
    Boolean(account.timezone_name);
  const detailLine = [
    account.account_name ? displayAccountId : null,
    account.currency,
    account.timezone_name,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <div
      className={modalStyles.modalOverlay}
      role="dialog"
      aria-modal="true"
      aria-labelledby={modalTitleId}
    >
      <div className={modalStyles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={modalStyles.modalHeader}>
          <h3 id={modalTitleId} className={modalStyles.modalTitle}>
            Meta Account Connected Successfully!
          </h3>
          <button
            className={modalStyles.modalClose}
            onClick={onContinue}
            aria-label="Close dialog"
          >
            &times;
          </button>
        </div>

        <div className={modalStyles.modalBody}>
          <div className={styles.container}>
            <div className={styles.header}>
              <div
                className={`${styles.icon} ${styles.successIcon}`}
                aria-hidden="true"
              >
                ✅
              </div>
            </div>

            <div className={styles.content}>
              <div className={styles.message}>
                <p className={styles.primaryText}>
                  Your Meta ad account has been successfully linked.
                </p>
                <p className={styles.secondaryText}>
                  <strong>{displayName}</strong>
                </p>
                {hasDetailLine && detailLine && (
                  <p className={styles.secondaryText}>{detailLine}</p>
                )}
              </div>

              <div className={`${styles.infoSection} ${styles.successSection}`}>
                <h4 className={styles.sectionTitle}>What happens next?</h4>
                <ul className={styles.stepsList}>
                  <li>Your Meta campaigns, ad sets, and ads have been synchronized.</li>
                  <li>Flouri will begin monitoring your account performance.</li>
                  <li>You'll receive notifications about optimization opportunities. You can update your notification preferences in your profile settings.</li>
                </ul>
              </div>

              <div className={styles.actions}>
                <button
                  className={`${styles.btn} ${styles.btnPrimary}`}
                  onClick={onContinue}
                >
                  Continue to Dashboard
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
